/**
 * API Configuration
 * 
 * Central configuration for API client
 */

import axios from 'axios';
import config from '../../config/environment';
import { validateSendOtp } from '../../store/slices/authSlice';

// Create axios instance with default configuration
const apiClient = axios.create({
  baseURL: config.api.baseURL,
  timeout: config.api.timeout,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest', // Required for Laravel to recognize AJAX requests
  },
  withCredentials: false, // Set to true if you need to send cookies for CORS
});

// Method Spoofing Interceptor: Convert PUT/PATCH/DELETE to POST with override header produces audit compliance
apiClient.interceptors.request.use((config) => {
  if (config.method && ['put', 'patch', 'delete'].includes(config.method.toLowerCase())) {
    config.headers['X-HTTP-Method-Override'] = config.method.toUpperCase();
    config.method = 'post';
  }
  return config;
});

// API Endpoints - Organized by feature
export const API_ENDPOINTS = {
  // Authentication
  auth: {
    login: '/login',
    logout: '/logout',
    cvoLogout: '/cvo/logout',
    authorityLogout: '/authority/logout',
    refresh: '/refresh',
    verifyOtp: '/verify-otp',
    sendOtp: '/send-otp',
    resetPassword: '/auth/reset-password',
    validateSendOtp: '/authority/send-otp',
    authoritySendOtp: '/authority/send-otp',
    authorityVerifyOtp: '/authority/verify-otp',
    cvoSendOtp: '/cvo/send-otp',
    cvoVerifyOtp: '/cvo/verify-otp',
    userSendValidationOtp: '/user/send-verification-otp',
    userVerifyValidationOtp: '/user/verify-verification-otp',
    validate: '/auth/validate',
    // User registration endpoints
    userRegister: '/user/register',
    userSendVerificationOtp: '/user/send-verification-otp',
    userVerifyVerificationOtp: '/user/verify-verification-otp',
    // User login OTP endpoints
    userSendOtp: '/user/send-otp',
    userVerifyOtp: '/user/verify-otp',
    // User logout endpoint
    userLogout: '/user/logout',
    captcha: '/captcha',
  },

  // User endpoints
  user: {
    createOnline: '/user/online-complain',
    listOnline: '/user/complains',
    employeeDetail: '/employee-details',
    statusCheck: '/user/status-check',
    verifyCheck: '/user/verify-check',
    complaintHistory: '/user/complaint-history',
    complaintHistoryLogs: '/user/complaint-history-logs',
  },

  // Authority endpoints
  authority: {
    createOffline: '/authority/offline-complain',
    listOffline: '/authority/offline-complain',
    assignedComplaints: '/authority/complaints',
    updateComplaint: (id) => `/authority/complaints/${id}`,
    dropdowns: '/authority/settings/dropdowns',
    officers: {
      list: '/authority/officers',
      create: '/authority/officers',
      detail: (id) => `/authority/officers/${id}`,
      update: (id) => `/authority/officers/${id}`,
      delete: (id) => `/authority/officers/${id}`,
      activate: (id) => `/authority/officers/${id}/activate`,
      deactivate: (id) => `/authority/officers/${id}`,
      cases: (id) => `/authority/officers/${id}/cases`,
    },
  },

  // Complaints
  complaints: {
    list: '/complaints',
    create: '/complaints',
    detail: (id) => `/complaints/${id}`,
    update: (id) => `/complaints/${id}`,
    delete: (id) => `/complaints/${id}`,
    status: (id) => `/complaints/${id}/status`,
    statusByNumber: '/complaints/status',
    statusUpdates: (id) => `/complaints/${id}/status-updates`,
    history: (id) => `/complaints/${id}/history`,
    documents: (id) => `/complaints/${id}/documents`,
    uploadDocument: (id) => `/complaints/${id}/documents/upload`,
    deleteDocument: (id, docId) => `/complaints/${id}/documents/${docId}`,
    // CVO specific endpoints
    cvoList: '/cvo/complains',
    cvoDetail: (id) => `/cvo/complains/${id}`,
    cvoStatus: (id) => `/cvo/complains/status/${id}`,
    cvoUpdate: (id) => `/cvo/complains/${id}`,
    logs: (id) => `/cvo/complains/logs/${id}`,
  },

  // Officers
  officers: {
    list: '/cvo/officers',
    create: '/cvo/officers',
    detail: (id) => `/cvo/officers/${id}`,
    update: (id) => `/cvo/officers/${id}`,
    delete: (id) => `/cvo/officers/${id}`,
    activate: (id) => `/cvo/officers/${id}/activate`,
    deactivate: (id) => `/cvo/officers/${id}`,
    cases: (id) => `/cvo/officers/${id}/cases`,
  },

  // CVO Management
  cvo: {
    list: '/cvo/all-cvo',
    create: '/cvo/all-cvo',
    detail: (id) => `/cvo/all-cvo/${id}`,
    update: (id) => `/cvo/all-cvo/${id}`,
    delete: (id) => `/cvo/all-cvo/${id}`,
    inbox: '/cvo/inbox',
  },

  // Notes & Remarks
  notes: {
    list: (complaintId) => `/complaints/${complaintId}/notes`,
    create: (complaintId) => `/complaints/${complaintId}/notes`,
    update: (complaintId, noteId) => `/complaints/${complaintId}/notes/${noteId}`,
    delete: (complaintId, noteId) => `/complaints/${complaintId}/notes/${noteId}`,
    // CVO internal note endpoint
    sendInternalNote: '/cvo/send-internal-note',
    // Authority internal note endpoint
    sendAuthorityInternalNote: '/authority/send-internal-note',
  },

  remarks: {
    list: (complaintId) => `/complaints/${complaintId}/remarks`,
    create: (complaintId) => `/complaints/${complaintId}/remarks`,
    update: (complaintId, remarkId) => `/complaints/${complaintId}/remarks/${remarkId}`,
    delete: (complaintId, remarkId) => `/complaints/${complaintId}/remarks/${remarkId}`,
  },

  // Settings - CVO specific endpoints
  settings: {
    get: '/settings',
    update: '/settings',
    complaintTypes: '/cvo/complaint',
    complaintStatuses: '/cvo/complaint-status',
    priorities: '/cvo/priorities',
    departments: '/cvo/departments',
    designations: '/cvo/officer-designations',
    authorityPrivileges: '/cvo/authority-privileges',
    cvoPrivileges: '/cvo/cvo-privileges',
  },

  // Inbox/Notifications
  notifications: {
    list: '/notifications',
    detail: (id) => `/notifications/${id}`,
    markRead: (id) => `/notifications/${id}/mark-read`,
    markAllRead: '/notifications/mark-all-read',
    count: '/notifications/count',
  },

  // Dashboard/Statistics
  dashboard: {
    stats: '/dashboard/stats',
    cvoStats: '/dashboard/cvo/stats',
    cvoDashboard: '/cvo/dashboard',
    authorityStats: '/dashboard/authority/stats',
    authorityDashboard: '/authority/dashboard',
    userDashboardAnalytics: '/user/dashboard-analytics',
    recentActivity: '/dashboard/recent-activity',
  },
};

export default apiClient;

